import enterpriseClient from '../clients/EnterpriseClient'
import { RiskAcceptanceStatus } from '../enums'
import { RiskAcceptance, Submission } from '../types'

/** Spread into home.tsx's `Step` union. */
export type RiskAcceptanceStep =
  | { name: 'readyToInvest' }
  | { name: 'acceptanceRejected' }
  | { name: 'riskAcceptance'; riskAcceptance: RiskAcceptance }

/** Newest by createdAt — UTC ISO-8601, so string comparison is safe. */
export const latestRiskAcceptance = (
  riskAcceptances: RiskAcceptance[],
): RiskAcceptance | undefined =>
  [...riskAcceptances].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]

/**
 * Rejected for THIS submission's cycle. The timestamp test is load-bearing: a
 * REJECTED row is never cleared, but the yearly sweep un-rejects the user.
 */
export const isRejectedThisCycle = (
  riskAcceptances: RiskAcceptance[],
  submission: Submission,
): boolean => {
  const latest = latestRiskAcceptance(riskAcceptances)

  return (
    latest?.status === RiskAcceptanceStatus.Rejected &&
    latest.createdAt > submission.createdAt
  )
}

/**
 * List first — create fails for an already-accepted or rejected user. The
 * ACCEPTED test is cycle-agnostic on purpose: any acceptance leaves them
 * approved.
 */
/** Renders straight into the prompt; a blank one would show a live Continue button over no content. */
const assertHasMessage = (riskAcceptance: RiskAcceptance): RiskAcceptance => {
  if (!riskAcceptance.message) {
    throw new Error(
      `Risk acceptance ${riskAcceptance.id} has no prompt message`,
    )
  }
  return riskAcceptance
}

export const resolveRiskAcceptanceStep = async (
  submission: Submission,
): Promise<RiskAcceptanceStep> => {
  const riskAcceptances = await enterpriseClient.listRiskAcceptances()

  if (riskAcceptances.some(a => a.status === RiskAcceptanceStatus.Accepted)) {
    return { name: 'readyToInvest' }
  }

  if (isRejectedThisCycle(riskAcceptances, submission)) {
    return { name: 'acceptanceRejected' }
  }

  const riskAcceptance = await enterpriseClient.createRiskAcceptance()

  return {
    name: 'riskAcceptance',
    riskAcceptance: assertHasMessage(riskAcceptance),
  }
}
